import React from 'react';
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Box,
  Grid,
  FormControl,
  Button,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";

const GarageRateConfiguration = ({
  setGarageRateType,
  setGarageSlabs,
  garageSlabs,
  garageRateType,
  selectValues,
  setSelectValues,
  setValue,
}) => {
  
  const handleGarageRateTypeChange = (event) => {
    const newType = event.target.value;
    setGarageRateType(newType);
    
    if (newType === 'slab') {
      // Reset to default slab when switching to slab-based
      setGarageSlabs([{ minKm: 0, maxKm: 5, rate: '' }]);
    }
  };

  const handleSlabChange = (index, field, value) => {
    const updatedSlabs = [...garageSlabs];
    
    if (field === 'maxKm' && index === garageSlabs.length - 1) {
      updatedSlabs[index][field] = value === '' ? null : parseInt(value);
    } else if (field === 'maxKm') {
      updatedSlabs[index][field] = parseInt(value);
    } else if (field === 'rate') {
      updatedSlabs[index][field] = value;
    }
    
    setGarageSlabs(updatedSlabs);
  };

  const addNewSlab = () => {
    const lastSlab = garageSlabs[garageSlabs.length - 1];
    const newMinKm = lastSlab.maxKm;

    if((lastSlab.minKm + 1) >= newMinKm) {
        return;
    }
    
    const newSlab = {
      minKm: newMinKm,
      maxKm: newMinKm + 4,
      rate: ''
    };
    
    // Make the previous last slab's maxKm non-editable by ensuring it's set
    const updatedSlabs = [...garageSlabs];
    if (updatedSlabs.length > 0) {
      const lastIndex = updatedSlabs.length - 1;
      if (updatedSlabs[lastIndex].maxKm === null || updatedSlabs[lastIndex].maxKm === '') {
        updatedSlabs[lastIndex].maxKm = newMinKm;
      }
    }
    
    setGarageSlabs([...updatedSlabs, newSlab]);
  };

  const removeLastSlab = () => {
    if (garageSlabs.length > 1) {
      const updatedSlabs = garageSlabs.slice(0, -1);
      // Make the new last slab editable
      if (updatedSlabs.length > 0) {
        updatedSlabs[updatedSlabs.length - 1].maxKm = updatedSlabs[updatedSlabs.length - 1].maxKm;
      }
      setGarageSlabs(updatedSlabs);
    }
  };
  
  if (!garageRateType) return null;

  return (
    <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <Typography variant="h8" sx={{ mb: 2 }} className='font-semibold'>
        Garage KM Configuration
      </Typography>
      
      {/* Rate Type Selection */}
      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <RadioGroup
          row
          value={garageRateType}
          onChange={handleGarageRateTypeChange}
        >
          <FormControlLabel 
            value="per_km" 
            control={<Radio />} 
            label="Per KM Rate"
            sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "16px",
                  fontWeight: 500,
                },
            }}
          />
          <FormControlLabel 
            value="slab" 
            control={<Radio />} 
            label="Slab Based"
            sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "16px",
                  fontWeight: 500,
                },
            }}
          />
        </RadioGroup>
      </FormControl>

      {/* Per KM Rate Input */}
      {garageRateType === 'per_km' && (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              type="number"
              value={selectValues?.garagePerKmRate || ''}
              onChange={(e) => {
                setSelectValues({
                  ...selectValues,
                  garagePerKmRate: e.target.value
                });
                setValue('garagePerKmRate', e.target.value);
              }}
              placeholder="Enter rate per KM"
            />
          </Grid>
        </Grid>
      )}

      {/* Slab Based Configuration */}
      {garageRateType === 'slab' && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Slab Range (KM)
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Rate (₹)
              </Typography>
            </Grid>
          </Grid>

          {garageSlabs.map((slab, index) => (
            <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'center' }}>
              <Grid item xs={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    disabled
                    size="small"
                    value={`${slab.minKm}<`}
                    sx={{ width: '80px' }}
                  />
                  <Typography>-</Typography>
                  {index === garageSlabs.length - 1 ? (
                    <TextField
                      size="small"
                      value={slab.maxKm === null || slab.maxKm === undefined ? '' : slab.maxKm}
                      onChange={(e) => handleSlabChange(index, 'maxKm', e.target.value)}
                      placeholder="Enter max"
                      sx={{ width: '80px' }}
                      type="number"
                      InputProps={{
                        startAdornment: (
                        <Typography 
                        sx={{
                          color: 'text.disabled',
                          userSelect: 'none',  
                          pointerEvents: 'none',
                          }}
                          >
                          {'≤'}
                        </Typography>
                        ),
                          inputProps: {
                          style: {
                            paddingLeft: '8px',
                            paddingRight: '8px',
                            boxSizing: 'border-box',
                          }
                        }
                      }}
                    />
                  ) : (
                    <TextField
                      disabled
                      size="small"
                      value={`≤${slab.maxKm}`}
                      sx={{ width: '80px' }}
                    />
                  )}
                </Box>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={slab.rate}
                  onChange={(e) => handleSlabChange(index, 'rate', e.target.value)}
                  placeholder="Enter rate"
                />
              </Grid>
              {index === garageSlabs.length - 1 && garageSlabs.length > 1 && (
                <Grid item xs={2}>
                  <Button
                    size="small"
                    color="error"
                    onClick={removeLastSlab}
                  >
                    <Typography
                        variant="body2"
                        className="fw400 pt20 alignItem onBoardDeleteBtn"
                        role="presentation"
                        
                        >
                        <span>
                            <DeleteOutlinedIcon role="presentation" />
                        </span>
                        Delete
                        </Typography>
                  </Button>
                </Grid>
              )}
            </Grid>
          ))}
          <Button
            variant="text"
            color="primary"
            onClick={addNewSlab}
            sx={{ mt: 1, textTransform: 'none' }}
            startIcon={<span>+</span>}
          >
            Add More Slabs
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default GarageRateConfiguration;
